import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { format, parseISO } from 'date-fns';
import { useState, type FC, type MouseEvent } from 'react';
import { DayPicker } from 'react-day-picker';

import 'react-day-picker/style.css';
import { MdClose, MdOutlineCalendarToday } from 'react-icons/md';

import { TAG_PICKER_PAPER_SX } from './tag-picker-panel';

const DEFAULT_TIME = '12:00';

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTE_STEPS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const pad2 = (n: number) => String(n).padStart(2, '0');

const timeSelectSx = {
  borderRadius: '10px',
  fontFamily: 'Figtree, sans-serif',
  fontSize: 13,
  bgcolor: 'common.white',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
} as const;

const timeSelectMenuProps = {
  slotProps: {
    paper: { sx: { borderRadius: '10px', maxHeight: 240, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } },
  },
} as const;

interface DateTimePickerPanelProps {
  /** Panel header, e.g. "Closes at" / "Schedule post". */
  title: string;
  /** Optional helper line under the header. */
  description?: string;
  /** datetime-local string ('yyyy-MM-ddTHH:mm') or '' for none — seeds the panel on mount. */
  value: string;
  /** Primary button label (default "Apply"). */
  applyLabel?: string;
  onApply: (value: string) => void;
  onClear: () => void;
}

/**
 * Future date + time picker panel: designed calendar (react-day-picker) with
 * hour/minute selects and an AM/PM toggle instead of the browser's native
 * datetime-local UI. Hosted in a popover by DateTimeField and by the
 * composer's schedule flow. Selection is pending until Apply.
 */
export const DateTimePickerPanel: FC<DateTimePickerPanelProps> = ({
  title,
  description,
  value,
  applyLabel = 'Apply',
  onApply,
  onClear,
}) => {
  const theme = useTheme();
  // Seeded on mount — the hosting popover unmounts the panel when closed, so
  // each open starts from the current value.
  const [pendingDate, setPendingDate] = useState<Date | undefined>(() => (value ? parseISO(value) : undefined));
  const [pendingTime, setPendingTime] = useState(() => (value ? format(parseISO(value), 'HH:mm') : DEFAULT_TIME));

  const combined = pendingDate ? parseISO(`${format(pendingDate, 'yyyy-MM-dd')}T${pendingTime}`) : undefined;
  const isFuture = !!combined && combined.getTime() > Date.now();

  // 12-hour parts for the time selects; stored canonically as 'HH:mm'.
  const [hour24, minute] = pendingTime.split(':').map(Number);
  const isPm = hour24 >= 12;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  // A minute carried in from an existing value may fall between the 5-minute
  // steps — keep it selectable instead of snapping it silently.
  const minuteOptions = MINUTE_STEPS.includes(minute) ? MINUTE_STEPS : [...MINUTE_STEPS, minute].sort((a, b) => a - b);

  const setTimeParts = (h12: number, min: number, pm: boolean) => {
    setPendingTime(`${pad2((h12 % 12) + (pm ? 12 : 0))}:${pad2(min)}`);
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ mb: 1, px: 0.5 }}>
        <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13, fontWeight: 700, color: '#333' }}>
          {title}
        </Typography>
        {description && (
          <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 12, color: '#808080', mt: '2px' }}>
            {description}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          px: 0.5,
          '& .rdp-root': {
            '--rdp-accent-color': theme.palette.primary.main,
            '--rdp-accent-background-color': `${theme.palette.primary.main}1A`,
            fontFamily: 'Figtree, sans-serif',
            fontSize: 14,
          },
        }}
      >
        <DayPicker mode="single" selected={pendingDate} onSelect={setPendingDate} disabled={{ before: new Date() }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, mt: 0.5 }}>
        <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13, color: '#555', mr: 'auto' }}>
          Time
        </Typography>
        <Select
          size="small"
          value={hour12}
          onChange={e => setTimeParts(Number(e.target.value), minute, isPm)}
          sx={timeSelectSx}
          MenuProps={timeSelectMenuProps}
        >
          {HOURS_12.map(h => (
            <MenuItem key={h} value={h} sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13 }}>
              {h}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 14, fontWeight: 700, color: '#808080' }}>
          :
        </Typography>
        <Select
          size="small"
          value={minute}
          onChange={e => setTimeParts(hour12, Number(e.target.value), isPm)}
          sx={timeSelectSx}
          MenuProps={timeSelectMenuProps}
        >
          {minuteOptions.map(m => (
            <MenuItem key={m} value={m} sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 13 }}>
              {pad2(m)}
            </MenuItem>
          ))}
        </Select>
        <Box
          sx={{
            display: 'flex',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            overflow: 'hidden',
          }}
        >
          {(['AM', 'PM'] as const).map(period => {
            const active = period === 'PM' ? isPm : !isPm;
            return (
              <Box
                key={period}
                role="button"
                tabIndex={0}
                onClick={() => setTimeParts(hour12, minute, period === 'PM')}
                sx={{
                  px: '10px',
                  py: '7px',
                  fontFamily: 'Figtree, sans-serif',
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  bgcolor: active ? `${theme.palette.primary.main}14` : 'transparent',
                  color: active ? 'primary.main' : '#666',
                  '&:hover': { bgcolor: active ? `${theme.palette.primary.main}14` : '#f7f7f7' },
                }}
              >
                {period}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pt: '12px',
          mt: 1.5,
          borderTop: '1px solid #f0f0f0',
          px: 0.5,
        }}
      >
        <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 12, color: '#808080' }}>
          {combined ? format(combined, 'MMM d, yyyy · h:mm a') : 'Pick a date'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClear}
            disabled={!value && !pendingDate}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              fontFamily: 'Figtree, sans-serif',
              fontWeight: 600,
              px: 2,
              color: '#666',
              borderColor: 'grey.300',
              '&:hover': { borderColor: 'grey.400', bgcolor: '#f7f7f7' },
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => combined && onApply(format(combined, "yyyy-MM-dd'T'HH:mm"))}
            disabled={!isFuture}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              fontFamily: 'Figtree, sans-serif',
              fontWeight: 600,
              px: 2.5,
              '&.Mui-disabled': { backgroundColor: '#E0E0E0', color: '#9E9E9E' },
            }}
          >
            {applyLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

interface DateTimeFieldProps {
  /** Popover header, e.g. "Closes at" / "Publish at". */
  label: string;
  /** Trigger text when empty; falls back to the label. */
  placeholder?: string;
  /** datetime-local string ('yyyy-MM-ddTHH:mm') or '' for none. */
  value: string;
  onChange: (value: string) => void;
}

/**
 * Input-like trigger (calendar icon + formatted value + inline clear) that
 * opens a DateTimePickerPanel. Used by the poll builder ("Closes at") and the
 * edit form's scheduling card ("Publish at").
 */
export const DateTimeField: FC<DateTimeFieldProps> = ({ label, placeholder, value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: '12px',
          height: 40,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'common.white',
          cursor: 'pointer',
          transition: 'border-color 0.15s ease',
          '&:hover': { borderColor: 'grey.400' },
        }}
      >
        <MdOutlineCalendarToday size={15} color="#808080" />
        <Typography
          sx={{
            fontFamily: 'Figtree, sans-serif',
            fontSize: 13,
            fontWeight: value ? 600 : 400,
            color: value ? '#333' : '#8a8a8a',
            whiteSpace: 'nowrap',
          }}
        >
          {value ? format(parseISO(value), 'MMM d, yyyy · h:mm a') : (placeholder ?? label)}
        </Typography>
        {value && (
          <IconButton
            size="small"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={e => {
              e.stopPropagation();
              onChange('');
            }}
            sx={{ p: '2px', ml: '-2px', color: '#909090', '&:hover': { color: '#555' } }}
          >
            <MdClose size={14} />
          </IconButton>
        )}
      </Box>
      <Popover
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { ...TAG_PICKER_PAPER_SX, width: 'auto' } } }}
      >
        <DateTimePickerPanel
          title={label}
          value={value}
          onApply={next => {
            onChange(next);
            setAnchorEl(null);
          }}
          onClear={() => {
            onChange('');
            setAnchorEl(null);
          }}
        />
      </Popover>
    </>
  );
};
